import { Skeleton } from "@/components/Skeleton";
import { useListMCPIntegrations, type MCPIntegration } from "@/queries/integration-queries";
import { useState, useMemo, useCallback, type FC } from "react";
import { AlertTriangle, XCircle } from "lucide-react";
import { IconCircleCheck } from "central-icons/IconCircleCheck";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import {
    type AgentExecution_Status_MCPIntegrationStatus,
    MCPIntegrationPhase,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import {
    useListHostAuthenticationTokens,
    useDeleteHostAuthenticationToken,
} from "@/queries/runner-configuration-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { useDesktop } from "@/hooks/use-desktop";
import { appMainService } from "@/ipc";
import type { PlainMessage } from "@bufbuild/protobuf";
import { Tooltip } from "@/components/Tooltip";
import { Switch, type SwitchState } from "@/components/Switch";
import type { HostAuthenticationToken } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";

type MCPIntegrationsListProps = {
    organizationId?: string;
    agentExecution: PlainAgentExecution;
};

type MCPServerStatus = PlainMessage<AgentExecution_Status_MCPIntegrationStatus> & {
    integration?: MCPIntegration;
    authToken?: PlainMessage<HostAuthenticationToken>;
};

type MCPItemProps = {
    mcpServer: MCPServerStatus;
    hasAuthToken: boolean;
};

const MCPItem: FC<MCPItemProps> = ({ mcpServer, hasAuthToken }) => {
    const { data: user } = useAuthenticatedUser();
    const { isDesktop } = useDesktop();
    const { isMobileViewport } = useIsMobileViewport();
    const deleteTokenMutation = useDeleteHostAuthenticationToken();
    const [isToggling, setIsToggling] = useState(false);
    const tooltipMessage = mcpServer.failureMessage || mcpServer.warningMessage;

    // Only show toggle for integrations (not standalone .ona MCP servers)
    const isIntegration = !!mcpServer.integration;

    // Determine switch state based on auth token and phase
    const getSwitchState = (): SwitchState => {
        if (isToggling) {
            return "pending";
        }

        if (!hasAuthToken) {
            return "unchecked";
        }

        return "checked";
    };

    const handleToggle = useCallback(
        async (checked: boolean) => {
            if (!user?.id || !mcpServer.integration) return;

            setIsToggling(true);

            try {
                if (checked) {
                    // Connect: Open OAuth authorization URL
                    let authUrl = `/integrations/oauth/${mcpServer.id}/authorize?principal=${encodeURIComponent(`user/${user.id}`)}`;

                    // Make authUrl absolute if it's relative
                    if (authUrl.startsWith("/")) {
                        authUrl = `${window.location.origin}${authUrl}`;
                    }

                    // Open in external browser for desktop app, otherwise use window.open
                    if (isDesktop && appMainService) {
                        void appMainService.openExternal({ url: authUrl });
                    } else {
                        window.open(authUrl, "_blank", "noopener");
                    }

                    // Reset toggling state after a short delay
                    setTimeout(() => {
                        setIsToggling(false);
                    }, 1000);
                } else {
                    // Disconnect: Delete auth token
                    if (!mcpServer.authToken) {
                        setIsToggling(false);
                        return;
                    }

                    await deleteTokenMutation.mutateAsync({
                        tokenId: mcpServer.authToken.id,
                    });
                }
            } catch (error) {
                console.error("Failed to toggle integration:", error);
            } finally {
                setIsToggling(false);
            }
        },
        [user?.id, isDesktop, mcpServer, deleteTokenMutation],
    );

    const itemContent = (
        <div className="flex w-full items-center justify-between gap-2 rounded-md p-2 transition-colors hover:bg-surface-secondary">
            <div className="flex items-center gap-2.5">
                {mcpServer.integration?.iconUrl && (
                    <img src={mcpServer.integration.iconUrl} alt={mcpServer.name} className="size-5 rounded" />
                )}
                <div className="flex items-center gap-1.5">
                    <span className="text-sm text-content-primary">{mcpServer.name}</span>
                </div>
            </div>
            <div className="flex items-center gap-2">
                {isIntegration && (
                    <Switch
                        state={getSwitchState()}
                        onToggle={handleToggle}
                        fixedWidth
                        aria-label={`Toggle ${mcpServer.name}`}
                        data-tracking-id={`mcp-integration-toggle-${mcpServer.id}`}
                    />
                )}
                {mcpServer.phase === MCPIntegrationPhase.MCP_INTEGRATION_PHASE_READY && (
                    <IconCircleCheck className="size-4 text-content-success" />
                )}
                {(mcpServer.failureMessage || mcpServer.warningMessage) && (
                    <span className="inline-flex items-center">
                        {mcpServer.failureMessage && <XCircle size={16} className="text-red-600 dark:text-red-400" />}
                        {mcpServer.warningMessage && (
                            <AlertTriangle size={16} className="text-yellow-600 dark:text-yellow-400" />
                        )}
                    </span>
                )}
            </div>
        </div>
    );

    if (isMobileViewport) {
        return (
            <div>
                {itemContent}
                {tooltipMessage && (
                    <div className="px-2 pb-2 pt-1">
                        <div className="rounded-md bg-surface-banner-warning-subtle p-2 text-xs text-content-secondary">
                            {tooltipMessage}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (tooltipMessage) {
        return <Tooltip content={tooltipMessage}>{itemContent}</Tooltip>;
    }

    return itemContent;
};

const LoadingState: FC = () => {
    return (
        <div className="flex flex-col gap-2">
            <Skeleton className="h-5 w-32" />
            <div className="flex flex-col gap-1">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
            </div>
        </div>
    );
};

const EmptyState: FC<{ message: string }> = ({ message }) => {
    return (
        <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
            <p className="text-sm text-content-secondary">{message}</p>
        </div>
    );
};

export const MCPIntegrationsList: FC<MCPIntegrationsListProps> = ({ organizationId, agentExecution }) => {
    const { isMobileViewport } = useIsMobileViewport();
    const { data: integrations, isLoading: integrationsLoading } = useListMCPIntegrations(organizationId);
    const { data: authTokensData, isLoading: tokensLoading } = useListHostAuthenticationTokens({ enabled: true });

    const isLoading = integrationsLoading || tokensLoading;

    // Merge integration data with status from agent execution
    const { mcpServersWithStatus, authTokenIntegrationIds } = useMemo(() => {
        if (!integrations || !authTokensData) {
            return {
                mcpServersWithStatus: [],
                authTokenIntegrationIds: new Set<string>(),
            };
        }

        const mcpStatuses = agentExecution.status?.mcpIntegrationStatuses || [];
        const authTokenIds = new Set(
            authTokensData.tokens.map((token) => token.integrationId).filter((id): id is string => !!id),
        );
        const tokensMap = new Map<string, PlainMessage<HostAuthenticationToken>>();
        authTokensData.tokens.forEach((token) => {
            if (token.integrationId) {
                tokensMap.set(token.integrationId, token);
            }
        });

        const serversWithStatus: MCPServerStatus[] = [];

        // Add all MCP integrations
        for (const integration of integrations) {
            const status = mcpStatuses.find((s) => s.id === integration.id);
            const authToken = tokensMap.get(integration.id);
            serversWithStatus.push({
                id: integration.id,
                name: integration.name,
                phase: status?.phase ?? MCPIntegrationPhase.MCP_INTEGRATION_PHASE_UNAVAILABLE,
                failureMessage: status?.failureMessage ?? "",
                warningMessage: status?.warningMessage ?? "",
                integration,
                authToken,
            });
        }

        // Add standalone MCP servers from .ona folder
        // These have empty id since they're not linked to any integration
        for (const status of mcpStatuses) {
            if (status.id === "") {
                serversWithStatus.push({
                    id: "",
                    name: status.name,
                    phase: status.phase,
                    failureMessage: status.failureMessage,
                    warningMessage: status.warningMessage,
                    // No integration property - this is a standalone MCP server
                });
            }
        }

        return { mcpServersWithStatus: serversWithStatus, authTokenIntegrationIds: authTokenIds };
    }, [integrations, authTokensData, agentExecution.status?.mcpIntegrationStatuses]);

    if (isLoading) {
        return <LoadingState />;
    }

    if (!mcpServersWithStatus || mcpServersWithStatus.length === 0) {
        return <EmptyState message="No MCP integrations configured" />;
    }

    return (
        <div className="flex flex-col rounded-xl focus-visible:outline" tabIndex={0}>
            {!isMobileViewport && <h3 className="p-2 text-sm font-medium text-content-muted">MCP Integrations</h3>}
            <div className="flex flex-col gap-1">
                {mcpServersWithStatus.map((mcpServer) => {
                    const itemKey = mcpServer.id || mcpServer.name;
                    return (
                        <MCPItem
                            key={itemKey}
                            mcpServer={mcpServer}
                            hasAuthToken={authTokenIntegrationIds.has(mcpServer.id)}
                            data-tracking-id={`integration-status-${itemKey}`}
                        />
                    );
                })}
            </div>
        </div>
    );
};
